import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDropzone } from "@/components/FileDropzone";
import { coladoTotal, concreteDiff, money, DAYS, DAY_LABEL } from "@/lib/api";

const empty = { obra: "", m2: "", price_per_m2: "", extra_amount: "", extra_description: "", notes: "", day: "", colador_id: "", concrete_m3: "", concrete_total: "", note_balance: "", concrete_apply: false };

export default function ColadoDialog({ open, onOpenChange, onSave, initial, coladores, mode = "backlog" }) {
  const [form, setForm] = useState(empty);
  const [pdf, setPdf] = useState({ path: null, name: null });

  useEffect(() => {
    if (open) {
      const base = initial || empty;
      setForm({
        obra: base.obra || "", m2: base.m2 ?? "", price_per_m2: base.price_per_m2 ?? "",
        extra_amount: base.extra_amount ?? "", extra_description: base.extra_description || "",
        notes: base.notes || "", day: base.day || "", colador_id: base.colador_id || "",
        concrete_m3: base.concrete_m3 ?? "", concrete_total: base.concrete_total ?? "",
        note_balance: base.note_balance ?? "", concrete_apply: base.concrete_apply || false,
      });
      setPdf({ path: base.pdf_file_path || null, name: base.pdf_file_name || null });
    }
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cForm = { m2: form.m2, price_per_m2: form.price_per_m2, extra_amount: form.extra_amount,
    concrete_total: form.concrete_total, note_balance: form.note_balance, concrete_apply: form.concrete_apply };
  const preview = coladoTotal(cForm);
  const diff = concreteDiff(cForm);
  const hasConcrete = Number(form.concrete_total) || Number(form.note_balance) || Number(form.concrete_m3);

  const save = () => {
    onSave({
      obra: form.obra,
      m2: Number(form.m2) || 0,
      price_per_m2: Number(form.price_per_m2) || 0,
      extra_amount: Number(form.extra_amount) || 0,
      extra_description: form.extra_description,
      notes: form.notes,
      day: form.day || null,
      colador_id: form.colador_id || null,
      concrete_m3: Number(form.concrete_m3) || 0,
      concrete_total: Number(form.concrete_total) || 0,
      note_balance: Number(form.note_balance) || 0,
      concrete_apply: !!form.concrete_apply,
      pdf_file_path: pdf.path,
      pdf_file_name: pdf.name,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="colado-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {initial?.id ? "Editar colado" : "Nuevo colado"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div>
            <Label>Obra / Descripción</Label>
            <Input value={form.obra} onChange={(e) => set("obra", e.target.value)}
              placeholder="Ej. Losa casa Sr. Pérez" className="mt-1.5" data-testid="colado-obra-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Metros² (m²)</Label>
              <Input type="number" step="any" value={form.m2} onChange={(e) => set("m2", e.target.value)}
                placeholder="50" className="mt-1.5" data-testid="colado-m2-input" />
            </div>
            <div>
              <Label>Precio por m²</Label>
              <Input type="number" step="any" value={form.price_per_m2} onChange={(e) => set("price_per_m2", e.target.value)}
                placeholder="120" className="mt-1.5" data-testid="colado-price-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Trabajo extra ($)</Label>
              <Input type="number" step="any" value={form.extra_amount} onChange={(e) => set("extra_amount", e.target.value)}
                placeholder="0" className="mt-1.5" data-testid="colado-extra-input" />
            </div>
            <div>
              <Label>Descripción del extra</Label>
              <Input value={form.extra_description} onChange={(e) => set("extra_description", e.target.value)}
                placeholder="Opcional" className="mt-1.5" data-testid="colado-extra-desc-input" />
            </div>
          </div>

          {mode === "assign" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Colador</Label>
                <Select value={form.colador_id} onValueChange={(v) => set("colador_id", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="colado-colador-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    {coladores.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Día</Label>
                <Select value={form.day} onValueChange={(v) => set("day", v)}>
                  <SelectTrigger className="mt-1.5" data-testid="colado-day-select"><SelectValue placeholder="Día" /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d) => <SelectItem key={d} value={d}>{DAY_LABEL[d]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          <div>
            <Label>Notas</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
              placeholder="Detalles adicionales…" className="mt-1.5" rows={2} data-testid="colado-notes-input" />
          </div>

          <div>
            <Label className="mb-1.5 block">PDF del colado (respaldo)</Label>
            <FileDropzone accept="application/pdf" kind="PDF" value={pdf.path} filename={pdf.name}
              onUploaded={(path, name) => setPdf({ path, name })}
              onClear={() => setPdf({ path: null, name: null })} testid="colado-pdf-drop" />
          </div>

          <div className="rounded-md border border-slate-200 p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <div>
                <Label className="text-slate-800">Concreto / Nota</Label>
                <p className="text-xs text-slate-400 leading-snug">Cuando el colador pide el concreto y cobra al cliente</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-slate-500">Sumar al total</span>
                <Switch checked={form.concrete_apply} onCheckedChange={(v) => set("concrete_apply", v)} data-testid="concrete-apply-switch" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs text-slate-500">m³ pedidos</Label>
                <Input type="number" step="any" value={form.concrete_m3} onChange={(e) => set("concrete_m3", e.target.value)} placeholder="0" className="mt-1" data-testid="concrete-m3-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Precio concreto</Label>
                <Input type="number" step="any" value={form.concrete_total} onChange={(e) => set("concrete_total", e.target.value)} placeholder="0" className="mt-1" data-testid="concrete-total-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Saldo de la nota</Label>
                <Input type="number" step="any" value={form.note_balance} onChange={(e) => set("note_balance", e.target.value)} placeholder="0" className="mt-1" data-testid="note-balance-input" />
              </div>
            </div>
            {hasConcrete ? (
              <div className={`flex items-center justify-between rounded-md px-3 py-2 text-sm ${diff >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="concrete-diff">
                <span>{diff >= 0 ? "A nuestro favor" : "Se le debe al colador"} <span className="text-xs opacity-70">(saldo − concreto)</span></span>
                <span className="font-semibold">{diff >= 0 ? "−" : "+"} {money(Math.abs(diff))} al total</span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between rounded-md bg-slate-900 px-4 py-3 text-white">
            <span className="text-sm text-slate-300">Total del colado</span>
            <span className="font-display text-xl font-bold" data-testid="colado-total-preview">{money(preview)}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="colado-cancel-btn">Cancelar</Button>
          <Button onClick={save} className="bg-orange-600 hover:bg-orange-700" data-testid="colado-save-btn">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
